import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import FileListItem from './FileListItem';
import { RootState } from '../../../../reducers';
import { fetchFiles, toggleSelectFile } from '../../librarySlice';
import FilesSkeleton from './FilesSkeleton';
import { RouteParams } from '../../types';
import { getFileUrl } from '../../../../lib/routingUtils';
import PendingUploads from '../../../upload/PendingUploads';
import FilesEmpty from '../FilesEmpty';
import { useQuery } from '../../../../hooks/urlHooks';
import FileListHeader from './FileListHeader';
import { useSelectMode } from '../../context/SelectModeContext';
import { FileType } from '../../../../types';
import useDebounce from '../../../../hooks/useDebounce';
import RecoveryFileListItem from '../RecoveryFileListItem';
import { loadBackupRecordsThunk } from '../../../settings/settingsSlice';
import { Table, TableBody, TableHeader } from '../../../../components/ui/table';
import { Card } from '../../../../components/ui/card';

interface FileListViewProps {
  liveRecordingsBanner?: React.ReactNode;
}

export const FileListView = React.memo(
  ({ liveRecordingsBanner }: FileListViewProps) => {
    const {
      library,
      upload,
      import: importState,
    } = useSelector((state: RootState) => state);
    const { activeUploads } = upload;
    const dispatch = useDispatch();
    const { teamId, folderId } = useParams<RouteParams>();
    const {
      searchQuery,
      sortBy = 'createdAt',
      sortOrder = 'desc',
    } = useQuery();
    const { files } = library;
    const { selectedFiles } = library;
    const { selectMode, setSelectMode } = useSelectMode();
    const { status } = files;
    const [isNearBottom, setIsNearBottom] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loadingPage, setLoadingPage] = useState<number | null>(null);
    const scrollHandlerRef = useRef<(() => void) | null>(null);
    const [shouldLoadMore, setShouldLoadMore] = useState(false);
    const [preloadedPages, setPreloadedPages] = useState<Set<number>>(
      new Set(),
    );

    // Reduce debounce time for faster preloading
    const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);

    const onFileSelect = useCallback(
      (file: FileType) => {
        // If not in select mode, enter it first
        if (!selectMode) {
          setSelectMode(true);
        }
        dispatch(toggleSelectFile(file));
      },
      [dispatch, selectMode, setSelectMode],
    );

    const handleScroll = useCallback(
      (event?: Event) => {
        // Try to find the scrollable container - could be window or a custom container
        const { documentElement } = document;
        let { scrollHeight, scrollTop, clientHeight } = documentElement;

        // If we have an event, try to use the target if it's scrollable
        if (event && event.target && event.target !== document) {
          const target = event.target as Element;
          if (target.scrollHeight > target.clientHeight) {
            ({ scrollHeight, scrollTop, clientHeight } = target);
          }
        }

        // Also check for common scrollable containers (including MUI Box)
        const scrollContainers = Array.from(
          document.querySelectorAll(
            '[style*="overflow: auto"], [style*="overflow-y: auto"], .MuiBox-root, [class*="overflow-auto"], [class*="overflow-y-auto"]',
          ),
        );
        const scrollableContainer = scrollContainers.find(
          (container) =>
            container.scrollHeight > container.clientHeight &&
            container.scrollTop >= 0,
        );

        if (scrollableContainer) {
          ({ scrollHeight, scrollTop, clientHeight } = scrollableContainer);
          console.log('🎯 Using scrollable container:', scrollableContainer);
        } else {
          console.log('📄 Using document element for scrolling');
        }

        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // Temporary debug logging
        if (Math.random() < 0.1) {
          // Only log occasionally to avoid spam
          console.log('🔄 Scroll Debug:', {
            scrollHeight,
            scrollTop,
            clientHeight,
            distanceFromBottom,
            preloadThreshold: window.innerHeight * 2,
            skeletonThreshold: window.innerHeight * 0.5,
            hasMoreFiles: files.pagination.total > files.data.length,
            filesStatus: files.status,
            isLoadingMore,
          });
        }
        // Preload when user is 2 viewport heights away from bottom for seamless scrolling
        const preloadThreshold = window.innerHeight * 2;
        // Show skeleton only when very close to bottom (half viewport)
        const skeletonThreshold = window.innerHeight * 0.5;

        // Update near-bottom state for skeleton display (much closer to bottom)
        setIsNearBottom(distanceFromBottom < skeletonThreshold);

        if (
          distanceFromBottom < preloadThreshold &&
          files.status !== 'pending' &&
          !isLoadingMore &&
          files.pagination.total > files.data.length
        ) {
          const nextPage = files.pagination.page + 1;
          const maxPages = Math.ceil(
            files.pagination.total / files.pagination.limit,
          );

          // Prevent duplicate page requests - only load if we haven't already loaded this page
          if (
            loadingPage === nextPage ||
            nextPage > maxPages ||
            preloadedPages.has(nextPage)
          ) {
            return;
          }

          // Trigger the debounced load instead of loading immediately
          setShouldLoadMore(true);
        } else {
          setShouldLoadMore(false);
        }
      },
      [files, isLoadingMore, loadingPage],
    );

    // Effect to handle the actual API call when debounced trigger fires
    useEffect(() => {
      if (debouncedShouldLoadMore && !isLoadingMore) {
        const nextPage = files.pagination.page + 1;
        const maxPages = Math.ceil(
          files.pagination.total / files.pagination.limit,
        );

        if (
          loadingPage !== nextPage &&
          nextPage <= maxPages &&
          !preloadedPages.has(nextPage)
        ) {
          setIsLoadingMore(true);
          setLoadingPage(nextPage);
          setPreloadedPages((prev) => new Set([...prev, nextPage]));
          dispatch(
            fetchFiles({
              teamId,
              folderId,
              page: nextPage,
              limit: files.pagination.limit, // Use the same limit as in pagination
              searchQuery,
              sortBy,
              sortOrder,
            }),
          );
        }
      }
    }, [
      debouncedShouldLoadMore,
      files,
      dispatch,
      teamId,
      folderId,
      searchQuery,
      sortBy,
      sortOrder,
      isLoadingMore,
      loadingPage,
    ]);

    useEffect(() => {
      // Reset loading state when dependencies change
      setIsLoadingMore(false);
      setLoadingPage(null);
      setPreloadedPages(new Set());
    }, [teamId, folderId, searchQuery, sortBy, sortOrder]);

    useEffect(() => {
      // Reset loading state when files fetch completes
      if (files.status !== 'pending') {
        setIsLoadingMore(false);
        setLoadingPage(null);
      }
    }, [files.status]);

    // Load backup records for recovery detection
    useEffect(() => {
      dispatch(loadBackupRecordsThunk());
    }, [dispatch]);

    useEffect(() => {
      // Store the handler reference for cleanup
      scrollHandlerRef.current = handleScroll;

      // Listen for scroll on window/document
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Also listen for scroll events on potential scrollable containers (including MUI Box)
      const scrollContainers = Array.from(
        document.querySelectorAll(
          '[style*="overflow: auto"], [style*="overflow-y: auto"], .MuiBox-root, [class*="overflow-auto"], [class*="overflow-y-auto"]',
        ),
      );

      const scrollListeners: Array<{
        element: Element;
        handler: (e: Event) => void;
      }> = [];

      scrollContainers.forEach((container) => {
        if (container.scrollHeight > container.clientHeight) {
          const containerHandler = (e: Event) => handleScroll(e);
          container.addEventListener('scroll', containerHandler, {
            passive: true,
          });
          scrollListeners.push({
            element: container,
            handler: containerHandler,
          });
        }
      });

      return () => {
        window.removeEventListener('scroll', handleScroll);

        // Clean up container listeners
        scrollListeners.forEach(({ element, handler }) => {
          element.removeEventListener('scroll', handler);
        });

        // Also remove with the ref in case the handler changed
        if (scrollHandlerRef.current) {
          window.removeEventListener('scroll', scrollHandlerRef.current);
        }
      };
    }, [handleScroll]);

    if (!teamId || !folderId) {
      return null;
    }

    if (
      files.data?.length === 0 &&
      activeUploads.length === 0 &&
      importState.status !== 'pending' &&
      status !== 'pending'
    ) {
      return <FilesEmpty />;
    }

    // Only show skeleton when actively loading and very close to bottom
    const showSkeleton =
      status === 'pending' ||
      (isLoadingMore &&
        isNearBottom &&
        files.pagination.total > files.data.length);

    return (
      <Card className="w-full overflow-hidden shadow-sm">
        <Table className="table-fixed">
          <TableHeader>
            <FileListHeader />
          </TableHeader>
          <TableBody>
            {/* Live Recordings Banner - Shows active live recordings at the top */}
            {liveRecordingsBanner}
            <PendingUploads view="list" />
            <RecoveryFileListItem />
            {files.data.map((file) => {
              const isSelected = !!selectedFiles[file._id];
              return (
                <FileListItem
                  file={file}
                  isSelected={isSelected}
                  selectMode={selectMode}
                  onSelect={onFileSelect}
                  fileUrl={getFileUrl({
                    teamId,
                    parentId: folderId,
                    fileId: file._id,
                    withBasePath: true,
                  })}
                  key={file._id}
                />
              );
            })}
            {showSkeleton && <FilesSkeleton />}
          </TableBody>
        </Table>
      </Card>
    );
  },
);

export default FileListView;
